import React from "react";
import {
	Button,
	ButtonProps,
	ChildrenProps,
	Column,
	DefaultContextValue,
	Grid,
	Heading,
	makeClassName,
	Modal,
	ModalProps,
	Row,
	Section,
	useRequiredContext,
} from "@hex-insights/core";
import { PermissionRequired } from "@hex-insights/permissioning";
import { Icon } from "../../../../Components";
import { permissions } from "../../../../Utilities";
import {
	AnnouncementCreateModalAnimatedIconController,
	AnnouncementCreatePermissionRequired,
} from "../../Announcement";
import {
	CalendarEventCreateModalAnimatedIconController,
	CalendarEventCreatePermissionRequired,
} from "../../CalendarEvent";
import { NewsletterCreateModalAnimatedIconController, NewsletterCreatePermissionRequired } from "../../Newsletter";
import { PostCreateModalAnimatedIconController, PostCreatePermissionRequired } from "../../Post";
import styles from "./styles.module.css";

const communicationCreatePermissions = [
	permissions.Announcement.Create.Admin,
	permissions.Announcement.Create.Basic,
	permissions.Newsletter.Create.Admin,
	permissions.Newsletter.Create.Basic,
	permissions.Post.Create.Admin,
	permissions.Post.Create.Basic,
	permissions.CalendarEvent.Create.Admin,
	permissions.CalendarEvent.Create.Basic,
];

const createPermissions = [...communicationCreatePermissions];

export function OmniCreateButtonPermissionRequired({ children }: Partial<ChildrenProps>) {
	return (
		<PermissionRequired requiredPermissions={createPermissions} checkMethod="or">
			{children}
		</PermissionRequired>
	);
}

export type OmniCreateModalContextValue = {
	isModalOpen: boolean;
	openModal: () => void;
	closeModal: () => void;
};

export const OmniCreateModalContext = React.createContext<OmniCreateModalContextValue>(
	new DefaultContextValue("OmniCreateModalContext") as any,
);

type OmniCreateModalContextManagerProps = Partial<ChildrenProps>;

export function OmniCreateModalContextManager({ children }: OmniCreateModalContextManagerProps) {
	const toggleState = Modal.useToggle();

	return (
		<OmniCreateModalContext.Provider value={toggleState}>
			{children}

			<Modal.If condition={toggleState.isModalOpen}>
				<OmniCreateModal onClose={toggleState.closeModal} />
			</Modal.If>
		</OmniCreateModalContext.Provider>
	);
}

export type OmniCreateButtonProps = Omit<ButtonProps, "onClick">;

export function OmniCreateButton({
	variant = "primary",
	children = (
		<Row justify="spaced-center" align="center" horizontalSpacing="0.375rem">
			<Icon.Plus size={16} /> Create
		</Row>
	),
	...props
}: OmniCreateButtonProps) {
	const { openModal } = useRequiredContext(OmniCreateModalContext);

	return (
		<Button variant={variant} onClick={openModal} {...props}>
			{children}
		</Button>
	);
}

const communicationGridResponsiveTracks = {
	0: 1,
	400: 2,
};

type OmniCreateModalProps = Pick<ModalProps, "ifRef" | "onClose" | "className" | "style">;

function OmniCreateModal({ ifRef, onClose, className, style }: OmniCreateModalProps) {
	const onSuccess = React.useCallback(() => {
		onClose?.();
	}, [onClose]);

	return (
		<Modal
			size="large"
			ifRef={ifRef}
			onClose={onClose}
			className={makeClassName(styles["omni-create-modal"], className)}
			style={style}
		>
			<Modal.Body>
				<Column justify="spaced-start" verticalSpacing="2rem">
					<PermissionRequired requiredPermissions={communicationCreatePermissions} checkMethod="or">
						<Section>
							<Section.Header>
								<Heading.H3 noMargin>Create a Post</Heading.H3>
							</Section.Header>
							<Section.Body>
								<Grid responsiveTracks={communicationGridResponsiveTracks} style={{ alignItems: "center" }} gap="1rem">
									<AnnouncementCreatePermissionRequired>
										<AnnouncementCreateModalAnimatedIconController onSuccess={onSuccess} style={{ width: "100%" }} />
									</AnnouncementCreatePermissionRequired>
									<NewsletterCreatePermissionRequired>
										<NewsletterCreateModalAnimatedIconController onSuccess={onSuccess} style={{ width: "100%" }} />
									</NewsletterCreatePermissionRequired>
									<PostCreatePermissionRequired>
										<PostCreateModalAnimatedIconController onSuccess={onSuccess} style={{ width: "100%" }} />
									</PostCreatePermissionRequired>
									<CalendarEventCreatePermissionRequired>
										<CalendarEventCreateModalAnimatedIconController onSuccess={onSuccess} style={{ width: "100%" }} />
									</CalendarEventCreatePermissionRequired>
								</Grid>
							</Section.Body>
						</Section>
					</PermissionRequired>
				</Column>
			</Modal.Body>
		</Modal>
	);
}
